import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/modern/components/ui/dropdown-menu';
import { TaskColorPicker } from './TaskColorPicker';
import { TaskStylePicker } from './TaskStylePicker';
import {
  canArchive,
  canDelete,
  canShare,
  canCopyToBoard,
  canExportHistory,
  canAdjustPoints,
} from '../contextMenuUtils';
import { openNewSpotIncentiveEditor } from '@/other/utils/utils';
import { DateHelper } from '@bryntum/schedulerpro';
import { Task } from '@/bryntum-lib/models/Task';
import { toast } from 'sonner';
import zipcelx from 'zipcelx';
import { useSwivuBryntumContext } from '@/react/hooks/useSwivuBryntumContext';

interface TaskContextMenuProps {
  record: Task;
  onClose?: () => void;
  onItemClick?: (item: { name: string; }) => void;
}

const TaskContextMenu: React.FC<TaskContextMenuProps> = ({ record, onClose, onItemClick }) => {
  const context = useSwivuBryntumContext();
  const { user, project, selectedBoard, boardManager, editorHandler } = context;

  const isDemoUser = user?.isDemoUser || false;

  // State to control which submenu is open (only one at a time)
  const [openSubmenu, setOpenSubmenu] = useState<'color' | 'style' | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear any pending close timeout
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Handle mouse enter - immediately open the submenu and cancel any pending close
  const handleMouseEnter = useCallback((submenu: 'color' | 'style') => {
    clearCloseTimeout();
    setOpenSubmenu(submenu);
  }, [clearCloseTimeout]);

  // Handle mouse leave - schedule a close after a delay, but only if the submenu is still open
  const handleMouseLeave = useCallback((submenu: 'color' | 'style') => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      // Only close if this submenu is still the one that's open
      setOpenSubmenu((current) => {
        if (current === submenu) {
          return null;
        }
        return current;
      });
      closeTimeoutRef.current = null;
    }, 150);
  }, [clearCloseTimeout]);

  // Handle content mouse enter - cancel any pending close
  const handleContentMouseEnter = useCallback(() => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  // Handle content mouse leave - close immediately (no delay)
  const handleContentMouseLeave = useCallback((submenu: 'color' | 'style') => {
    clearCloseTimeout();
    setOpenSubmenu((current) => {
      if (current === submenu) {
        return null;
      }
      return current;
    });
  }, [clearCloseTimeout]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearCloseTimeout();
    };
  }, [clearCloseTimeout]);

  // Permission checks
  const showArchive = canArchive(user, record);
  const showDelete = canDelete(user, record);
  const showShare = canShare(user, isDemoUser, record);
  const showCopyTo = canCopyToBoard(user, isDemoUser, selectedBoard, boardManager?.boardNameStore);
  const showExportHistory = canExportHistory(user, record);
  const showAdjustPoints = canAdjustPoints(user, project, record);
  const showColorPicker = !record?.isPhantom;
  const showStylePicker = !record?.isPhantom;

  // Action handlers
  const handleArchive = () => {
    onItemClick?.({ name: 'archive' });
  };

  const handleDelete = () => {
    onItemClick?.({ name: 'delete' });
  };

  const handleShare = () => {
    onItemClick?.({ name: 'share' });
  };

  const handleCopyTo = () => {
    onItemClick?.({ name: 'copyTo' });
  };

  const handleExportHistory = () => {
    try {
      // Get the task's schedule events
      const events = record?.events || [];

      if (events.length === 0) {
        toast.warning('No scheduling data to export');
        onClose?.();
        return;
      }

      // Prepare data for Excel export
      const config = {
        filename: `Task_History_${record?.name || 'Export'}`,
        sheet: {
          data: [
            // Header row
            [
              { value: 'Resource', type: 'string' as const },
              { value: 'Description', type: 'string' as const },
              { value: 'Start Date', type: 'string' as const },
              { value: 'Start Time', type: 'string' as const },
              { value: 'End Date', type: 'string' as const },
              { value: 'End Time', type: 'string' as const },
              { value: 'Duration', type: 'string' as const },
            ],
            // Data rows
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ...events.map((event: any) => {
              const startDate = event.startDate ? new Date(event.startDate) : null;
              const endDate = event.endDate ? new Date(event.endDate) : null;

              // Calculate duration in milliseconds
              let duration = 'N/A';
              if (startDate && endDate) {
                const deltaMs = endDate.getTime() - startDate.getTime();
                duration = DateHelper.formatDelta(deltaMs, { abbrev: true }) as string;
              }

              return [
                { value: event.resource?.name || 'N/A', type: 'string' as const },
                { value: event.resourceDescription || 'N/A', type: 'string' as const },
                { value: startDate ? DateHelper.format(startDate, 'YYYY-MM-DD') : 'N/A', type: 'string' as const },
                { value: startDate ? DateHelper.format(startDate, 'HH:mm') : 'N/A', type: 'string' as const },
                { value: endDate ? DateHelper.format(endDate, 'YYYY-MM-DD') : 'N/A', type: 'string' as const },
                { value: endDate ? DateHelper.format(endDate, 'HH:mm') : 'N/A', type: 'string' as const },
                { value: duration, type: 'string' as const },
              ];
            }),
          ],
        },
      };

      // Export to Excel
      zipcelx(config);

      toast.success('Task history exported successfully');

      onClose?.();
    } catch (error) {
      console.error('Error exporting task history:', error);
      toast.error('Failed to export task history');
    }
  };

  const handleAdjustPoints = () => {
    openNewSpotIncentiveEditor({
      project,
      editorHandler,
      otherParams: { selectedTask: record },
    });
    onClose?.();
  };

  return (
    <>
      {/* Task Color Picker */}
      <TaskColorPicker
        record={record}
        disabled={!showColorPicker}
        onColorChange={() => {onClose?.()}}
        open={openSubmenu === 'color'}
        onOpenChange={(open) => {
          if (open) {
            clearCloseTimeout();
            setOpenSubmenu('color');
          } else {
            handleMouseLeave('color');
          }
        }}
        onMouseEnter={() => handleMouseEnter('color')}
        onMouseLeave={() => handleMouseLeave('color')}
        onContentMouseEnter={handleContentMouseEnter}
        onContentMouseLeave={() => handleContentMouseLeave('color')}
      />

      {/* Task Style Picker */}
      <TaskStylePicker
        record={record}
        disabled={!showStylePicker}
        onStyleChange={() => {onClose?.()}}
        open={openSubmenu === 'style'}
        onOpenChange={(open) => {
          if (open) {
            clearCloseTimeout();
            setOpenSubmenu('style');
          } else {
            handleMouseLeave('style');
          }
        }}
        onMouseEnter={() => handleMouseEnter('style')}
        onMouseLeave={() => handleMouseLeave('style')}
        onContentMouseEnter={handleContentMouseEnter}
        onContentMouseLeave={() => handleContentMouseLeave('style')}
      />

      {/* Separator */}
      <DropdownMenuSeparator />

      {/* Share */}
      <DropdownMenuItem onClick={handleShare} disabled={!showShare}>
        <i className="fa-light fa-share w-4 h-4" />
        <span>Share</span>
      </DropdownMenuItem>

      {/* Copy to */}
      <DropdownMenuItem onClick={handleCopyTo} disabled={!showCopyTo}>
        <i className="fa-light fa-copy w-4 h-4" />
        <span>Copy to</span>
      </DropdownMenuItem>

      {/* Export History */}
      <DropdownMenuItem onClick={handleExportHistory} disabled={!showExportHistory}>
        <i className="fa-light fa-file-export w-4 h-4" />
        <span>Export Task History</span>
      </DropdownMenuItem>

      {/* Adjust Points */}
      <DropdownMenuItem onClick={handleAdjustPoints} disabled={!showAdjustPoints}>
        <i className="fa-light fa-star w-4 h-4" />
        <span>Adjust Points</span>
      </DropdownMenuItem>

      {/* Separator before Archive/Delete */}
      <DropdownMenuSeparator />

      {/* Archive/Unarchive */}
      <DropdownMenuItem onClick={handleArchive} disabled={!showArchive}>
        <i className="fa-light fa-folder-open w-4 h-4" />
        <span>{record?.archived ? 'Unarchive' : 'Archive'}</span>
      </DropdownMenuItem>

      {/* Delete */}
      <DropdownMenuItem onClick={handleDelete} className="text-destructive" disabled={!showDelete}>
        <i className="fa-light fa-trash w-4 h-4" />
        <span>Delete</span>
      </DropdownMenuItem>
    </>
  );
};

export default TaskContextMenu;
